import { useState, useEffect, useRef, useCallback } from 'react';
import { Button, Input, Textarea, Select, Modal } from './ui';
import RecurrencePicker from './RecurrencePicker';


// ── useSpeechRecognition ──────────────────────────────────────────────────
function useSpeechRecognition({ onResult, onError }) {
  const [listening, setListening]   = useState(false);
  const [supported, setSupported]   = useState(false);
  const recognitionRef              = useRef(null);

  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    setSupported(!!SR);
  }, []);

  const start = useCallback(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { onError?.('Speech recognition not supported in this browser.'); return; }

    const r = new SR();
    r.lang            = 'en-US';
    r.interimResults  = true;
    r.maxAlternatives = 1;
    r.continuous      = false;

    r.onstart  = () => setListening(true);
    r.onend    = () => setListening(false);
    r.onerror  = (e) => { setListening(false); onError?.(e.error === 'not-allowed' ? 'Microphone access denied.' : 'Could not understand. Try again.'); };
    r.onresult = (e) => {
      const transcript = Array.from(e.results)
        .map(r => r[0].transcript)
        .join('');
      onResult?.(transcript, e.results[e.results.length - 1].isFinal);
    };

    recognitionRef.current = r;
    r.start();
  }, [onResult, onError]);

  const stop = useCallback(() => {
    recognitionRef.current?.stop();
    setListening(false);
  }, []);

  const toggle = useCallback(() => {
    listening ? stop() : start();
  }, [listening, start, stop]);

  return { listening, supported, toggle, stop };
}

function MicIcon({ active }) {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      {active ? (
        // Stop/mute icon when active
        <>
          <line x1="1" y1="1" x2="23" y2="23"/>
          <path d="M9 9v3a3 3 0 005.12 2.12M15 9.34V4a3 3 0 00-5.94-.6"/>
          <path d="M17 16.95A7 7 0 015 12v-2m14 0v2a7 7 0 01-.11 1.23"/>
          <line x1="12" y1="19" x2="12" y2="23"/>
          <line x1="8" y1="23" x2="16" y2="23"/>
        </>
      ) : (
        // Normal mic icon
        <>
          <path d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z"/>
          <path d="M19 10v2a7 7 0 01-14 0v-2"/>
          <line x1="12" y1="19" x2="12" y2="23"/>
          <line x1="8" y1="23" x2="16" y2="23"/>
        </>
      )}
    </svg>
  );
}

// ── Quick Add Bar ─────────────────────────────────────────────────────────
export function QuickAdd({ onAdd, categories = [] }) {
  const [val,     setVal]     = useState('');
  const [pri,     setPri]     = useState('medium');
  const [focused, setFocused] = useState(false);
  const [adding,  setAdding]  = useState(false);
  const [micError, setMicError] = useState('');
  const inputRef = useRef(null);

  const { listening, supported: micSupported, toggle: toggleMic } = useSpeechRecognition({
    onResult: (transcript, isFinal) => {
      setVal(transcript);
      setMicError('');
      if (isFinal && transcript.trim()) {
        // Auto-focus input so user can review before submitting
        setTimeout(() => inputRef.current?.focus(), 100);
      }
    },
    onError: (msg) => {
      setMicError(msg);
      setTimeout(() => setMicError(''), 3500);
    },
  });

  const submit = async () => {
    const trimmed = val.trim();
    if (!trimmed || adding) return;
    setAdding(true);
    try {
      await onAdd({ title: trimmed, priority: pri, due: 'today' });
      setVal('');
      setPri('medium');
      inputRef.current?.focus();
    } finally {
      setAdding(false);
    }
  };

  const PRI_OPTS = [
    { v: 'high',   c: '#DC2626', label: 'P1' },
    { v: 'medium', c: '#D97706', label: 'P2' },
    { v: 'low',    c: '#16A34A', label: 'P3' },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', marginBottom: 8 }}>
      <style>{`@keyframes micPulse{0%,100%{opacity:1;transform:scale(1)}50%{opacity:0.6;transform:scale(1.15)}}`}</style>

      {/* Input row */}
      <div style={{
        border: `1px solid ${focused ? 'var(--accent)' : 'var(--border)'}`,
        borderRadius: 10, overflow: 'hidden',
        background: 'var(--surface)', transition: 'border-color 0.15s',
        boxShadow: focused ? '0 0 0 3px var(--accent-ring)' : 'none',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px' }}>
          {/* + icon */}
          <span style={{
            width: 16, height: 16, display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: focused ? 'var(--accent)' : 'var(--text3)', fontSize: 18, lineHeight: 1,
            flexShrink: 0, transition: 'color 0.15s', fontWeight: 300,
          }}>+</span>

          {/* Text input */}
          <input
            ref={inputRef}
            value={val}
            onChange={e => setVal(e.target.value)}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            onKeyDown={e => e.key === 'Enter' && submit()}
            placeholder="Add a task…"
            style={{
              flex: 1, border: 'none', outline: 'none', background: 'transparent',
              fontSize: 14, color: 'var(--text)', fontFamily: 'inherit',
            }}
          />

          {/* Priority pills — always visible */}
          <div style={{ display: 'flex', gap: 3, alignItems: 'center' }}>
            {PRI_OPTS.map(p => (
              <button key={p.v} onClick={() => setPri(p.v)} style={{
                background: pri === p.v ? p.c + '18' : 'transparent',
                border: `1px solid ${pri === p.v ? p.c : 'transparent'}`,
                borderRadius: 5, cursor: 'pointer',
                color: pri === p.v ? p.c : 'var(--text3)',
                fontWeight: 700, fontSize: 10,
                padding: '2px 6px', fontFamily: 'inherit', transition: 'all 0.1s',
                opacity: pri === p.v ? 1 : 0.45,
              }}>{p.label}</button>
            ))}
          </div>

          {/* Mic button */}
          {micSupported && (
            <button
              onClick={toggleMic}
              title={listening ? 'Stop recording' : 'Speak to add task'}
              style={{
                width: 30, height: 30, borderRadius: 8, border: 'none',
                background: listening ? '#EF4444' : 'var(--bg)',
                color: listening ? 'white' : 'var(--text3)',
                cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0, transition: 'all 0.15s',
                outline: listening ? '2px solid rgba(239,68,68,0.3)' : 'none',
                animation: listening ? 'micPulse 1.2s ease-in-out infinite' : 'none',
              }}
            >
              <MicIcon active={listening} />
            </button>
          )}

          {/* Add button */}
          {val.trim() && (
            <button onClick={submit} disabled={adding} style={{
              background: 'var(--accent)', border: 'none', borderRadius: 6,
              color: 'white', fontWeight: 600, fontSize: 12,
              padding: '5px 12px', cursor: 'pointer',
              opacity: adding ? 0.6 : 1, fontFamily: 'inherit', transition: 'all 0.15s',
              flexShrink: 0,
            }}>{adding ? '…' : 'Add'}</button>
          )}
        </div>
      </div>

      {/* Mic error */}
      {micError && (
        <div style={{
          marginTop: 6, padding: '6px 10px', borderRadius: 7,
          background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)',
          fontSize: 12, color: '#DC2626', display: 'flex', alignItems: 'center', gap: 6,
        }}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          {micError}
        </div>
      )}

      {/* Listening indicator */}
      {listening && (
        <div style={{
          marginTop: 6, padding: '7px 12px', borderRadius: 8,
          background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.15)',
          display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#DC2626',
        }}>
          <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#EF4444', display: 'inline-block', animation: 'micPulse 1s ease-in-out infinite', flexShrink: 0 }} />
          Listening… speak your task
        </div>
      )}
    </div>
  );
}
// ── Full Task Modal ────────────────────────────────────────────────────────
const PRIORITY_OPTIONS = [
  { value: 'high',   label: 'P1 — High'   },
  { value: 'medium', label: 'P2 — Medium' },
  { value: 'low',    label: 'P3 — Low'    },
];

const DUE_OPTIONS = [
  { value: 'today', label: 'Today' },
  { value: 'later', label: 'Later' },
];

const EMPTY_FORM = {
  title         : '',
  notes         : '',
  priority      : 'medium',
  due           : 'today',
  category_id   : '',
  due_date      : '',
  recurrence_type  : 'none',
  recurrence_config: null,
};

export function TaskModal({ open, onClose, onSave, task, categories = [] }) {
  const [form,   setForm]   = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (task) {
      // Parse stored config string → object for the picker
      let cfg = task.recurrence_config ?? null;
      if (typeof cfg === 'string') {
        try { cfg = JSON.parse(cfg); } catch { cfg = null; }
      }

      setForm({
        title            : task.title          || '',
        notes            : task.notes          || '',
        priority         : task.priority       || 'medium',
        due              : task.due            || 'today',
        category_id      : task.category?.id   || task.category_id || '',
        due_date         : task.due_date        || '',
        recurrence_type  : task.recurrence_type || 'none',
        recurrence_config: cfg,
      });
    } else {
      setForm(EMPTY_FORM);
    }
    setErrors({});
  }, [task, open]);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleRecurrenceChange = ({ type, config }) => {
    setForm(p => ({ ...p, recurrence_type: type, recurrence_config: config }));
  };

  const validate = () => {
    const e = {};
    if (!form.title.trim()) e.title = 'Task name is required';
    if (form.recurrence_type === 'weekly' && !(form.recurrence_config?.days?.length)) {
      e.recurrence = 'Select at least one day for weekly repeat';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      // Serialize config back to JSON string for API
      const payload = {
        ...form,
        category_id      : form.category_id || null,
        recurrence_config: form.recurrence_config
          ? JSON.stringify(form.recurrence_config)
          : null,
      };
      await onSave(payload);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const catOptions = [
    { value: '', label: 'No category' },
    ...categories.map(c => ({ value: c.id, label: `${c.icon || ''} ${c.name}`.trim() })),
  ];

  return (
    <Modal open={open} onClose={onClose} title={task ? 'Edit task' : 'Add task'}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <Input
          placeholder="Task name"
          value={form.title}
          onChange={e => set('title', e.target.value)}
          error={errors.title}
          autoFocus
          style={{ fontSize: 15, fontWeight: 500 }}
        />
        <Textarea
          placeholder="Description (optional)"
          value={form.notes}
          onChange={e => set('notes', e.target.value)}
          rows={2}
        />

        {/* Priority + Due row */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <Select label="Priority" value={form.priority} onChange={e => set('priority', e.target.value)} options={PRIORITY_OPTIONS} />
          {/* Due — radio pills */}
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text3)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Due</label>
            <div style={{ display: 'flex', gap: 6 }}>
              {DUE_OPTIONS.map(opt => (
                <button key={opt.value} type="button"
                  onClick={() => set('due', opt.value)}
                  style={{
                    flex: 1, padding: '9px 8px', borderRadius: 8, border: 'none',
                    cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, fontWeight: 600,
                    background: form.due === opt.value ? 'var(--accent-light)' : 'var(--bg)',
                    color: form.due === opt.value ? 'var(--accent)' : 'var(--text2)',
                    outline: form.due === opt.value ? '1.5px solid var(--accent)' : '1.5px solid var(--border)',
                    transition: 'all 0.12s',
                  }}>
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Category + Due Date row */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <Select label="Category" value={form.category_id} onChange={e => set('category_id', e.target.value)} options={catOptions} />
          {/* Due date with clear button */}
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text3)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Due Date</label>
            <div style={{ position: 'relative' }}>
              <input type="date" value={form.due_date}
                onChange={e => set('due_date', e.target.value)}
                style={{
                  width: '100%', boxSizing: 'border-box',
                  padding: form.due_date ? '9px 32px 9px 11px' : '9px 11px',
                  fontSize: 13, fontFamily: 'inherit',
                  border: '1.5px solid var(--border)', borderRadius: 8,
                  background: 'var(--input-bg)', color: form.due_date ? 'var(--text)' : 'var(--text3)',
                  outline: 'none', cursor: 'pointer',
                }}
              />
              {form.due_date && (
                <button type="button" onClick={() => set('due_date', '')}
                  style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text3)', padding: 2, display: 'flex', lineHeight: 1 }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Recurrence picker */}
        <div>
          <p style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Repeat
          </p>
          <RecurrencePicker
            value={{ type: form.recurrence_type, config: form.recurrence_config }}
            onChange={handleRecurrenceChange}
          />
          {errors.recurrence && (
            <p style={{ fontSize: 12, color: '#EF4444', marginTop: 4 }}>{errors.recurrence}</p>
          )}
        </div>

        <div style={{
          display: 'flex', gap: 8, justifyContent: 'flex-end',
          marginTop: 6, paddingTop: 14, borderTop: '1px solid var(--border)',
        }}>
          <Button variant="ghost" onClick={onClose} size="sm">Cancel</Button>
          <Button onClick={handleSave} disabled={saving} size="sm">
            {saving ? 'Saving…' : task ? 'Save changes' : 'Add task'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}